<template>
    <v-container class="pa-0 student-profile">
        <back-button class="mb-3" />

        <skeleton-loader v-if="loading" type="student-profile" />

        <div v-else class="ficha-grid">

            <!-- LEFT -->
            <div class="left-col">
                <div class="student-card">
                    <v-img v-if="studentImg" :src="studentImg" aspect-ratio="1" class="student-photo" />
                    <div v-else class="student-photo student-photo--initials" :style="avatarGradient">
                        {{ initials }}
                    </div>
                    <div class="student-info">
                        <div class="student-name">{{ studentName }}</div>
                        <div v-if="subLabel" class="student-sub">{{ subLabel }}</div>
                        <div class="student-tags">
                            <span class="tag" :class="isActive ? 'tag-active' : 'tag-inactive'">{{ statusLabel }}</span>
                            <span v-if="sportTag" class="tag tag-sport">{{ sportTag }}</span>
                        </div>
                    </div>
                </div>

                <student-statistics :docs="monthRoutines" />

                <ai-alert-card :student-id="studentId" />
                <ai-recommendation-card ref="recommendationCard" :student-id="studentId" />
            </div>

            <!-- CENTER -->
            <div class="center-col">
                <div class="acc-group">
                    <expanded-sections :sections="sections" eager>
                        <template #data>
                            <basic-info-profile :client="student" />
                        </template>
                        <template #action-objectives>
                            <accordion-add-button v-if="isCoach" icon="mdi-pencil-outline" size="16"
                                :tooltip="`${$t('common.edit')} ${$t('common.objectives')}`"
                                @click="$refs.objectivesProfile.showObjectivesDialog()" />
                        </template>
                        <template #objectives>
                            <objectives-profile ref="objectivesProfile" :student="student"
                                @saved="loadStudent(false)" />
                        </template>
                        <template #action-injuries>
                            <accordion-add-button :tooltip="`${$t('common.add')} ${$t('common.injury')}`"
                                @click="$refs.injuries.showInjuryForm()" />
                        </template>
                        <template #injuries>
                            <injuries-card ref="injuries" :coachId="coachId" :student-id="studentId" addButton
                                header-add />
                        </template>
                        <template #action-evaluations>
                            <accordion-add-button v-if="isCoach" :tooltip="$t('common.new_evaluation')"
                                @click="$refs.reports.showCreateReport()" />
                        </template>
                        <template #evaluations>
                            <reports-card ref="reports" :student-id="studentId" :student="student"
                                :coach-id="coachId" />
                        </template>
                        <template #implements>
                            <equipments-profile :equipments="equipments" noTitle />
                        </template>
                        <template #action-files>
                            <accordion-add-button v-if="filesCount < 5"
                                :tooltip="`${$t('common.add')} ${$t('common.file')}`"
                                @click="$refs.files.showAddFile()" />
                        </template>
                        <template #files>
                            <files-student-section ref="files" :coachId="coachId" :student-id="studentId"
                                header-add @count="filesCount = $event" />
                        </template>
                        <template #action-notes>
                            <accordion-add-button v-if="isCoach" :tooltip="`${$t('common.add')} ${$t('common.note')}`"
                                @click="$refs.notes.showAddNote()" />
                        </template>
                        <template #notes>
                            <notes-student-section ref="notes" :student-id="studentId" header-add />
                        </template>
                    </expanded-sections>
                </div>

                <wellness-chart class="wellness-section" :docs="monthRoutines" />
            </div>

            <!-- RIGHT -->
            <div class="right-col">
                <recent-chat-profile :student="student" />
                <calendar-student :student-id="studentId" />
                <v-btn outlined block color="error" class="danger-btn text-none" @click="showDeleteSubscription">
                    <v-icon size="16" left>mdi-account-remove-outline</v-icon>
                    {{ $t('coach.delete_student_subscription') }}
                </v-btn>
            </div>

        </div>

        <!-- REMOVE STUDENT FROM ROSTER -->
        <kimsa-confirm ref="deleteSubscriptionDialog" alert-mode show-cancel
            :title="$t('coach.delete_student_subscription_title')"
            :content="$t('coach.delete_student_subscription_body')"
            :confirm-label="$t('coach.delete_student_subscription')"
            :loading="deletingSubscription" :persistent="false"
            @confirm="confirmSubscriptionDelete" :close-on-confirm="false" />

        <ai-chat-drawer
            :student-id="studentId"
            :student="student"
            :month-routines="monthRoutines"
        />

    </v-container>
</template>

<script>

import { db } from '@/utils/Firebase'
import { mapActions, mapState } from "vuex";
import { sportLabel } from '@/utils/Taxonomy'
import { getInitials, getAvatarGradient } from '@/utils/Common'
import KimsaConfirm from "@/components/common/KimsaConfirm";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import BasicInfoProfile from "@/components/elements/profile/BasicInfoProfile";
import EquipmentsProfile from "@/components/elements/profile/EquipmentsProfile";
import StudentStatistics from "@/components/student/StudentStatistics";
import WellnessChart from "@/components/statistics/WellnessChart";
import RecentChatProfile from "@/components/elements/profile/RecentChatProfile";
import InjuriesCard from "@/components/student/InjuriesCard";
import CalendarStudent from '../../components/common/CalendarStudent.vue';
import BackButton from '../../components/common/BackButton.vue';
import NotesStudentSection from '../../components/coach/NotesStudentSection.vue';
import ExpandedSections from '../../components/common/ExpandedSections.vue';
import ObjectivesProfile from '../../components/elements/profile/ObjectivesProfile.vue';
import FilesStudentSection from '../../components/coach/FilesStudentSection.vue';
import ReportsCard from '../../components/student/ReportsCard.vue';
import AccordionAddButton from '../../components/common/AccordionAddButton.vue';
import { setStudentGroup, setStudentTeam } from '@/utils/Exercises'
import AiChatDrawer from '@/components/coach/ai/AiChatDrawer'
import AiRecommendationCard from '@/components/coach/ai/AiRecommendationCard'
import AiAlertCard from '@/components/coach/ai/AiAlertCard'

export default {
    components: {
        InjuriesCard,
        RecentChatProfile,
        WellnessChart,
        StudentStatistics,
        EquipmentsProfile,
        BasicInfoProfile,
        SkeletonLoader,
        KimsaConfirm,
        CalendarStudent,
        BackButton,
        NotesStudentSection,
        ExpandedSections,
        ObjectivesProfile,
        FilesStudentSection,
        ReportsCard,
        AccordionAddButton,
        AiChatDrawer,
        AiRecommendationCard,
        AiAlertCard,
    },
    data() {
        return {
            loading: false,
            student: null,
            monthRoutines: [],
            deletingSubscription: false,
            filesCount: 0,
        }
    },
    computed: {
        ...mapState(['equipment']),
        sections(vm = this) {
            return [
                { title: vm.$t('common.data'), value: 'data' },
                { title: vm.$t('common.objectives'), value: 'objectives' },
                { title: vm.$t('common.injuries'), value: 'injuries' },
                { title: vm.$t('common.evaluations'), value: 'evaluations' },
                { title: vm.$t('common.implements'), value: 'implements' },
                { title: vm.$t('common.files'), value: 'files' },
                { title: vm.$t('common.notes'), value: 'notes' },
            ]
        },
        studentId(vm = this) {
            return vm.$route.params.uid || null
        },
        studentName(vm = this) {
            return vm.student?.name || ''
        },
        studentImg(vm = this) {
            return vm.student?.avatar || null
        },
        initials(vm = this) {
            return getInitials(vm.studentName)
        },
        avatarGradient(vm = this) {
            return getAvatarGradient(vm.studentId || vm.studentName || '')
        },
        isCoach(vm = this) {
            return vm.accountType === 'coach' || vm.accountType === 'company'
        },
        isActive(vm = this) {
            return !vm.student?.studentSubscription?.paused
        },
        statusLabel(vm = this) {
            return vm.isActive ? vm.$t('common.active') : vm.$t('common.inactive')
        },
        sportTag(vm = this) {
            return vm.student?.sportSelected ? sportLabel(vm.student.sportSelected) : null
        },
        sinceLabel(vm = this) {
            const seconds = vm.student?.createdAt?.seconds
            if (!seconds) return null
            return `${vm.$t('common.since')} ${vm.$moment(seconds * 1000).format('MMMM YYYY')}`
        },
        ageLabel(vm = this) {
            const birth = vm.student?.birthDate?.seconds
            if (!birth) return null
            return `${vm.$moment().diff(vm.$moment(birth * 1000), 'years')} ${vm.$t('common.years')}`
        },
        subLabel(vm = this) {
            return [vm.sinceLabel, vm.ageLabel].filter(Boolean).join(' · ')
        },
        equipments(vm = this) {
            if (!Array.isArray(vm.student?.equipments)) return []
            return vm.equipment?.filter(equipment => vm.student.equipments.includes(equipment.name)) || []
        },
        coachId(vm = this) {
            return vm.student?.studentSubscription?.coachId || null
        }
    },
    async mounted() {
        const vm = this
        await vm.loadStudent()
        await vm.loadMonthRoutines()
        if (vm.equipment.length === 0) await vm.bindEquipment()
        if (vm.$route.query.scrollTo === 'recommendation') {
            vm.$nextTick(() => {
                vm.$refs.recommendationCard?.$el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            })
        }
    },
    methods: {
        ...mapActions(['bindEquipment']),
        async loadStudent(needLoading = true) {
            const vm = this
            await vm.withLoading(async () => {
                let studentRef = await db.collection('users').doc(vm.studentId).get()
                vm.student = { id: vm.studentId, ...studentRef.data() }
            }, needLoading ? 'loading' : null)
        },
        async loadMonthRoutines() {
            const vm = this

            let startOfMonth = vm.$moment().startOf('month').format('YYYY-MM-DD')
            let endOfMonth = vm.$moment().endOf('month').format('YYYY-MM-DD')
            let monthRoutinesRef = await db.collection('assignedRoutines')
                .where('studentId', '==', vm.studentId)
                .where('rawDate.full', ">=", startOfMonth)
                .where('rawDate.full', "<=", endOfMonth)
                .orderBy('rawDate.full', 'asc').get()

            vm.monthRoutines = monthRoutinesRef.docs.map(routine => {
                return { id: routine.id, ...routine.data() }
            })
        },
        showDeleteSubscription() {
            const vm = this
            vm.$refs.deleteSubscriptionDialog.show()
        },
        async confirmSubscriptionDelete() {
            const vm = this
            await vm.withLoading(async () => {
                let subscriptionId = vm.student?.studentSubscription?.id || null
                if (subscriptionId) await db.collection('subscriptions').doc(subscriptionId).delete()

                const payload = { studentSubscription: null }

                if (vm.student?.group) {
                    await setStudentGroup(vm.studentId, null, vm.student.group, { skipUserUpdate: true })
                    payload.group = null
                }
                if (vm.student?.team) {
                    await setStudentTeam(vm.studentId, null, vm.student.team, { skipUserUpdate: true })
                    payload.team = null
                }

                await db.collection('users').doc(vm.studentId).update(payload)

                vm.$refs.deleteSubscriptionDialog?.hide()
                vm.toast(vm.$t('coach.student_subscription_deleted'))

                vm.replaceRoute({
                    name: 'coach.students',
                    query: { deletedStudentAccount: vm.studentId }
                })
            }, 'deletingSubscription')
        },
    },
}
</script>

<style scoped>
.ficha-grid {
    display: grid;
    grid-template-columns: 260px 1fr 280px;
    gap: 20px;
    align-items: start;
}

.left-col,
.center-col,
.right-col {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

/* student card */
.student-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.student-photo {
    width: 100%;
    display: block;
}

.student-photo--initials {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    font-weight: 800;
    letter-spacing: 0.02em;
}

.student-info {
    padding: 14px 16px 16px;
}

.student-name {
    font-size: 18px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-bottom: 4px;
}

.student-sub {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-bottom: 10px;
}

.student-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.tag {
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 20px;
    letter-spacing: 0.04em;
}

.tag-active {
    background: rgba(51, 153, 153, 0.15);
    color: var(--v-accent-base);
}

.tag-inactive {
    background: rgba(232, 92, 92, 0.15);
    color: var(--v-error-base);
}

.tag-sport {
    background: rgba(0, 153, 102, 0.15);
    color: #009966;
}

/* center */
.acc-group {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.wellness-section {
    min-height: 320px;
}

/* danger */
.danger-btn {
    margin-top: 4px;
    height: 42px !important;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: normal;
    border-color: rgba(232, 92, 92, 0.35) !important;
}

@media (max-width: 1263px) {
    .ficha-grid {
        grid-template-columns: 220px 1fr 240px;
        gap: 14px;
    }
}

@media (max-width: 959px) {
    .ficha-grid {
        grid-template-columns: 1fr;
        gap: 14px;
    }
}
</style>
